import * as React from "react";
import { useState } from "react";

import { withTypes } from "react-final-form";
import { Field } from "react-final-form";
import axios from "axios";

import {
  Button,
  CardActions,
  CircularProgress,
  TextField,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  Grid,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";
import {
  Notification,
  useTranslate,
  useNotify,
  DateInput,
  minValue,
  maxValue,
} from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";
import CustomConfirm from 'ra-custom-confirm';
import Check from '@material-ui/icons/Check';
import ErrorOutline from '@material-ui/icons/ErrorOutline';

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
    padding: "3em 2em",
  },
  container: {
    width: "100%",
    maxWidth: "900px",
  },
  pageTitle: {
    marginBottom: "2em",
    textAlign: "center",
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  dateCard: {
    marginBottom: "2em",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    borderRadius: "12px",
  },
  form: {
    padding: "2em",
  },
  input: {
    marginBottom: "1.5em",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    "& > div": {
      width: "100%",
      maxWidth: "350px",
    },
    "& .MuiInputBase-root": {
      backgroundColor: "white",
    },
  },
  actions: {
    padding: "0",
    display: "flex",
    justifyContent: "center",
  },
  checkButton: {
    padding: "12px 48px",
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(63, 81, 181, 0.3)",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 6px 16px rgba(63, 81, 181, 0.4)",
      transform: "translateY(-2px)",
    },
  },
  amountDisplay: {
    marginBottom: "2em",
    padding: "2.5em",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(102, 126, 234, 0.4)",
    textAlign: "center",
    color: "white",
  },
  amountLabel: {
    color: "rgba(255,255,255,0.9)",
    marginBottom: "0.5em",
    fontSize: "1.1rem",
    fontWeight: 500,
  },
  amountValue: {
    color: "white",
    fontWeight: 700,
    fontSize: "3rem",
    textShadow: "0 2px 8px rgba(0,0,0,0.2)",
  },
  amountRow: {
    marginBottom: "0.75em",
    "&:last-of-type": { marginBottom: 0 },
  },
  guidanceNote: {
    marginTop: "1.5em",
    padding: "1em 1.25em",
    backgroundColor: "rgba(255,255,255,0.2)",
    borderRadius: "8px",
    borderLeft: "4px solid rgba(255,255,255,0.9)",
    fontSize: "0.95rem",
    lineHeight: 1.5,
    textAlign: "left",
  },
  guidanceTitle: {
    fontWeight: 600,
    marginBottom: "0.5em",
    opacity: 1,
  },
  section: {
    marginBottom: "2em",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    borderRadius: "12px",
    overflow: "hidden",
  },
  sectionHeader: {
    padding: "1.5em 2em",
    backgroundColor: theme.palette.grey[50],
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  sectionContent: {
    padding: "2em",
  },
  transferSection: {
    display: "flex",
    flexDirection: "column",
    gap: "1.5em",
  },
  transferGroup: {
    padding: "1.5em",
    backgroundColor: theme.palette.grey[50],
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
  },
  amountInput: {
    marginBottom: "1em",
    "& .MuiInputBase-root": {
      backgroundColor: "white",
    },
  },
  button: {
    padding: "12px 24px",
    fontSize: "0.95rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "8px",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    },
  },
  outlinedButton: {
    padding: "12px 24px",
    fontSize: "0.95rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "8px",
    borderWidth: "2px",
    transition: "all 0.3s ease",
    "&:hover": {
      borderWidth: "2px",
      transform: "translateY(-2px)",
    },
  },
  balanceDisplay: {
    marginTop: "1.5em",
    padding: "1.5em",
    backgroundColor: "#e8f5e9",
    borderRadius: "8px",
    border: "2px solid #4caf50",
  },
  balanceText: {
    fontSize: "1.2rem",
    color: "#2e7d32",
    fontWeight: 600,
  },
  divider: {
    margin: "1.5em 0",
  },
}));

interface FormValues {
  date?: string;
  masterToRewardsAmount?: string;
  rewardsToMasterAmount?: string;
}

const { Form } = withTypes<FormValues>();

const renderInput = ({
  meta: { touched, error } = { touched: false, error: undefined },
  input: { ...inputProps },
  ...props
}: any) => (
  <TextField
    error={!!(touched && error)}
    helperText={touched && error}
    {...inputProps}
    {...props}
    fullWidth
  />
);

const MasterToUserWalletDashboard = () => {
  const translate = useTranslate();
  const classes = useStyles();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [checkingBalance, setCheckingBalance] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [updatingFailure, setUpdatingFailure] = useState(false);
  const [amountDifference, setAmountDifference] = useState<number | null>(null);
  const [expectedAmount, setExpectedAmount] = useState<number | null>(null);
  const [receivedAmount, setReceivedAmount] = useState<number | null>(null);
  const [masterBalance, setMasterBalance] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [transferType, setTransferType] = useState<"masterToRewards" | "rewardsToMaster" | null>(null);
  const [transferAmount, setTransferAmount] = useState<string>("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [transferData, setTransferData] = useState<any>(null);
  const currentDate = moment().format("YYYY-MM-DD");

  const handleDateSubmit = (inputData: any) => {
    setLoading(true);
    setAmountDifference(null);
    setExpectedAmount(null);
    setReceivedAmount(null);
    notify("Fetching amount difference...", "info");

    let config = getConfigGet({ date: inputData.date });
    setSelectedDate(inputData.date);

    axios
      .get(ENDPOINTS.MASTER_TO_USER_EWALLET_DASHBOARD, config)
      .then((res: any) => {
        setLoading(false);
        // Backend returns: [[["Expected Amount","Amount Received From Bps"],["3264.00",3246]]]
        const responseData = res?.data;
        let difference = null;
        let expected: number | null = null;
        let received: number | null = null;
        
        // Parse the nested array format
        if (Array.isArray(responseData) && responseData.length > 0) {
          // Check for the format [[["header1", "header2"], ["value1", "value2"]]]
          const outerArray = responseData[0];
          if (Array.isArray(outerArray) && outerArray.length === 2) {
            const headers = outerArray[0];
            const values = outerArray[1];
            
            if (Array.isArray(headers) && Array.isArray(values) && values.length >= 2) {
              // Expected Amount is at index 0, Amount Received From Bps is at index 1
              expected = parseFloat(String(values[0]));
              received = parseFloat(String(values[1]));
              
              if (!isNaN(expected) && !isNaN(received)) {
                difference = expected - received;
              }
            }
          }
        }
        
        // Fallback: try to find amountDifference property
        if (difference === null) {
          const flatData = Array.isArray(responseData) ? responseData : [responseData];
          for (const item of flatData) {
            if (item && typeof item === 'object' && 'amountDifference' in item) {
              difference = item.amountDifference;
              break;
            }
          }
        }
        
        setExpectedAmount(expected);
        setReceivedAmount(received);
        setAmountDifference(difference);
        if (difference !== null && difference !== undefined) {
          notify(`Amount difference: £${difference.toFixed(2)}`, "info", "", undefined, 5000);
        } else {
          notify("No data found for the selected date", "warning", "", undefined, 5000);
        }
      })
      .catch((error) => {
        setLoading(false);
        notify(
          error?.response?.data?.message || "Failed to get amount difference",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  const handleCheckMasterBalance = () => {
    setCheckingBalance(true);
    notify("Checking master balance...", "info");

    let config = getConfigGet({ balance: true });

    axios
      .get(ENDPOINTS.MASTER_TO_USER_EWALLET_DASHBOARD, config)
      .then((res: any) => {
        setCheckingBalance(false);
        // Backend returns: [[["Master Wallet Balance"], [masterAccountBalance]]]
        // The response is an array containing: [["Master Wallet Balance"], [balance]]
        const responseData = Array.isArray(res?.data) ? res?.data : [res?.data];
        let balance = null;
        
        // Look for the balance in the nested array format
        for (const item of responseData) {
          if (Array.isArray(item) && item.length === 2) {
            // Check if it matches the format [["Master Wallet Balance"], [balance]]
            if (Array.isArray(item[0]) && Array.isArray(item[1]) && item[1].length > 0) {
              // item[0] should be ["Master Wallet Balance"]
              // item[1] should be [balance]
              const potentialBalance = item[1][0];
              if (typeof potentialBalance === 'number') {
                balance = potentialBalance;
                break;
              }
            }
          }
        }
        
        // Fallback: try to find balance in other formats
        if (balance === null) {
          balance = res?.data?.balance || res?.data?.[0]?.balance || res?.data || null;
        }
        
        setMasterBalance(balance);
        if (balance !== null && balance !== undefined) {
          notify(`Master balance: £${balance.toFixed(2)}`, "info", "", undefined, 5000);
        } else {
          notify("Could not retrieve master balance", "warning", "", undefined, 5000);
        }
      })
      .catch((error) => {
        setCheckingBalance(false);
        notify(
          error?.response?.data?.message || "Failed to check master balance",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  const handleUpdateFailure = () => {
    setUpdatingFailure(true);
    notify("Updating master to user ewallet failure...", "info");

    let headers = getConfigPost();
    let data = {
      date: selectedDate || currentDate,
    };

    axios
      .patch(ENDPOINTS.UPDATE_MASTER_TO_USER_EWALLET_FAILURE, data, { headers })
      .then((res: any) => {
        setUpdatingFailure(false);
        notify(res?.data?.data || res?.data?.message || "Successfully updated", "info", "", undefined, 5000);
      })
      .catch((error) => {
        setUpdatingFailure(false);
        notify(
          error?.response?.data?.message || "Failed to update master to user ewallet failure",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  const handleTransferClick = (type: "masterToRewards" | "rewardsToMaster", amount: string) => {
    if (!amount || parseFloat(amount) <= 0) {
      notify("Please enter a valid amount", "error", "", undefined, 5000);
      return;
    }

    setTransferType(type);
    setTransferAmount(amount);
    setTransferData({
      type: type === "masterToRewards" ? "Master to Rewards" : "Rewards to Master",
      amount: parseFloat(amount),
      date: selectedDate || currentDate,
    });
    setConfirmOpen(true);
  };

  const handleTransferConfirm = () => {
    if (!transferType || !transferAmount) return;

    setTransferring(true);
    setConfirmOpen(false);

    let headers = getConfigPost();
    let data = {
      amount: parseFloat(transferAmount),
      date: selectedDate || currentDate,
    };

    const endpoint =
      transferType === "masterToRewards"
        ? ENDPOINTS.TRANSFER_MASTER_TO_REWARDS
        : ENDPOINTS.TRANSFER_REWARDS_TO_MASTER;

    axios
      .post(endpoint, data, { headers })
      .then((res: any) => {
        setTransferring(false);
        notify(res?.data?.data || res?.data?.message || "Transfer successful", "info", "", undefined, 5000);
        // Reset amount difference to refresh data
        if (selectedDate) {
          handleDateSubmit({ date: selectedDate });
        }
        setTransferAmount("");
      })
      .catch((error) => {
        setTransferring(false);
        notify(
          error?.response?.data?.message || "Transfer failed",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  const handleTransferCancel = () => {
    setConfirmOpen(false);
    setTransferType(null);
    setTransferAmount("");
    setTransferData(null);
  };

  const validate = (values: FormValues) => {
    const errors: FormValues = {};
    if (!values.date) {
      errors.date = translate("ra.validation.required");
    }
    if (values.masterToRewardsAmount) {
      const amount = parseFloat(values.masterToRewardsAmount);
      if (isNaN(amount) || amount <= 0) {
        errors.masterToRewardsAmount = "Please enter a valid positive amount";
      }
    }
    if (values.rewardsToMasterAmount) {
      const amount = parseFloat(values.rewardsToMasterAmount);
      if (isNaN(amount) || amount <= 0) {
        errors.rewardsToMasterAmount = "Please enter a valid positive amount";
      }
    }
    return errors;
  };

  // Enable transfer if there's any difference (positive or negative)
  const hasDifference = amountDifference !== null && amountDifference !== 0;

  return (
    <>
      <Form
        onSubmit={handleDateSubmit}
        validate={validate}
        render={({ handleSubmit, values }) => (
          <div>
            <form onSubmit={handleSubmit} noValidate>
              <div className={classes.main}>
                <div className={classes.container}>
                  <Typography variant="h4" className={classes.pageTitle}>
                    Master to User Wallet Dashboard
                  </Typography>

                  <Card className={classes.dateCard}>
                    <CardContent className={classes.form}>
                      <div className={classes.input}>
                        <DateInput
                          source="date"
                          validate={[minValue("2021-01-01"), maxValue(currentDate)]}
                          label="Select Date"
                        />
                      </div>
                      <CardActions className={classes.actions}>
                        <Button
                          variant="contained"
                          type="submit"
                          color="primary"
                          disabled={loading}
                          className={classes.checkButton}
                        >
                          {loading && <CircularProgress size={20} thickness={2} style={{ marginRight: "10px" }} />}
                          Check Amount Difference
                        </Button>
                      </CardActions>
                    </CardContent>
                  </Card>

                  {amountDifference !== null && (
                    <Paper className={classes.amountDisplay} elevation={0}>
                      <Typography variant="h6" className={classes.amountLabel}>
                        Amount Summary
                      </Typography>
                      {(expectedAmount !== null && receivedAmount !== null) ? (
                        <>
                          <Typography variant="body1" className={classes.amountRow} style={{ opacity: 0.95 }}>
                            <strong>Expected:</strong> £{expectedAmount.toFixed(2)}
                          </Typography>
                          <Typography variant="body1" className={classes.amountRow} style={{ opacity: 0.95 }}>
                            <strong>Received:</strong> £{receivedAmount.toFixed(2)}
                          </Typography>
                          <Typography variant="h2" className={classes.amountValue} style={{ marginTop: "0.25em" }}>
                            £{amountDifference.toFixed(2)}
                          </Typography>
                          <Typography variant="body2" style={{ marginTop: "0.25em", opacity: 0.9 }}>
                            {amountDifference > 0 ? "Expected amount is higher" : amountDifference < 0 ? "Received amount is higher" : "Amounts are balanced"}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="h2" className={classes.amountValue}>
                            £{amountDifference.toFixed(2)}
                          </Typography>
                          <Typography variant="body2" style={{ marginTop: "0.5em", opacity: 0.9 }}>
                            {amountDifference > 0 ? "Expected amount is higher" : amountDifference < 0 ? "Received amount is higher" : "Amounts are balanced"}
                          </Typography>
                        </>
                      )}
                      <Box className={classes.guidanceNote} component="div">
                        <Typography variant="subtitle2" className={classes.guidanceTitle} style={{ color: "white" }}>
                          What to do
                        </Typography>
                        <Typography variant="body2" style={{ color: "white", opacity: 0.95 }}>
                          If <strong>Expected</strong> is more than <strong>Received</strong> → use <strong>Rewards to Master Wallet</strong> transfer.
                        </Typography>
                        <Typography variant="body2" style={{ color: "white", opacity: 0.95, marginTop: "0.35em" }}>
                          If <strong>Received</strong> is more than <strong>Expected</strong> → use <strong>Master to Rewards Wallet</strong> transfer.
                        </Typography>
                      </Box>
                    </Paper>
                  )}

                  {hasDifference && (
                    <Paper className={classes.section} elevation={2}>
                      <div className={classes.sectionHeader}>
                        <Typography variant="h6" className={classes.sectionTitle}>
                          Transfer Funds
                        </Typography>
                      </div>
                      <div className={classes.sectionContent}>
                        <div className={classes.transferSection}>
                          <div className={classes.transferGroup}>
                            <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: "1em" }}>
                              Master to Rewards Wallet
                            </Typography>
                            <Field
                              name="masterToRewardsAmount"
                              component={renderInput}
                              label="Amount (£)"
                              type="number"
                              className={classes.amountInput}
                              disabled={transferring}
                            />
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={transferring || !values.masterToRewardsAmount}
                              onClick={() =>
                                handleTransferClick("masterToRewards", values.masterToRewardsAmount || "")
                              }
                              className={classes.button}
                              fullWidth
                            >
                              {transferring && transferType === "masterToRewards" && (
                                <CircularProgress size={18} thickness={2} style={{ marginRight: "10px" }} />
                              )}
                              Transfer to Rewards
                            </Button>
                          </div>

                          <div className={classes.transferGroup}>
                            <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: "1em" }}>
                              Rewards to Master Wallet
                            </Typography>
                            <Field
                              name="rewardsToMasterAmount"
                              component={renderInput}
                              label="Amount (£)"
                              type="number"
                              className={classes.amountInput}
                              disabled={transferring}
                            />
                            <Button
                              variant="contained"
                              color="secondary"
                              disabled={transferring || !values.rewardsToMasterAmount}
                              onClick={() =>
                                handleTransferClick("rewardsToMaster", values.rewardsToMasterAmount || "")
                              }
                              className={classes.button}
                              fullWidth
                            >
                              {transferring && transferType === "rewardsToMaster" && (
                                <CircularProgress size={18} thickness={2} style={{ marginRight: "10px" }} />
                              )}
                              Transfer to Master
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Paper>
                  )}

                  <Paper className={classes.section} elevation={2}>
                    <div className={classes.sectionHeader}>
                      <Typography variant="h6" className={classes.sectionTitle}>
                        Additional Actions
                      </Typography>
                    </div>
                    <div className={classes.sectionContent}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Button
                            variant="outlined"
                            color="primary"
                            disabled={updatingFailure}
                            onClick={handleUpdateFailure}
                            className={classes.outlinedButton}
                            fullWidth
                          >
                            {updatingFailure && <CircularProgress size={18} thickness={2} style={{ marginRight: "10px" }} />}
                            Update Master to User Ewallet Failure
                          </Button>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Button
                            variant="outlined"
                            color="secondary"
                            disabled={checkingBalance}
                            onClick={handleCheckMasterBalance}
                            className={classes.outlinedButton}
                            fullWidth
                          >
                            {checkingBalance && <CircularProgress size={18} thickness={2} style={{ marginRight: "10px" }} />}
                            Check Master Balance
                          </Button>
                        </Grid>
                      </Grid>

                      {masterBalance !== null && (
                        <Box className={classes.balanceDisplay}>
                          <Typography variant="h6" className={classes.balanceText}>
                            <span role="img" aria-label="money bag">💰</span> Master Balance: £{masterBalance.toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                    </div>
                  </Paper>

                  <Notification />
                </div>
              </div>
            </form>

            <CustomConfirm
              isOpen={confirmOpen}
              title="Please verify the transfer details"
              user={transferData}
              content={CustomConfirmContent}
              confirm="Confirm"
              confirmColor="primary"
              ConfirmIcon={Check}
              cancel="Cancel"
              CancelIcon={ErrorOutline}
              onConfirm={handleTransferConfirm}
              onClose={handleTransferCancel}
            />
          </div>
        )}
      />
    </>
  );
};

const CustomConfirmContent = (props: any) => {
  const data = props.user;
  if (!data) return null;

  return (
    <div style={{ padding: "1.5em" }}>
      <Box style={{ 
        padding: "1.5em", 
        backgroundColor: "#f5f7fa", 
        borderRadius: "8px",
        marginBottom: "1em"
      }}>
        <Typography variant="body1" gutterBottom style={{ marginBottom: "0.8em" }}>
          <strong>Transfer Type:</strong> <span style={{ color: "#667eea" }}>{data.type}</span>
        </Typography>
        <Typography variant="body1" gutterBottom style={{ marginBottom: "0.8em" }}>
          <strong>Amount:</strong> <span style={{ color: "#2e7d32", fontSize: "1.2rem", fontWeight: 600 }}>£{data.amount.toFixed(2)}</span>
        </Typography>
        <Typography variant="body1">
          <strong>Date:</strong> {data.date}
        </Typography>
      </Box>
      <Typography variant="body2" color="textSecondary" style={{ textAlign: "center", fontStyle: "italic" }}>
        <span role="img" aria-label="warning">⚠️</span> Are you sure you want to proceed with this transfer?
      </Typography>
    </div>
  );
};

export default MasterToUserWalletDashboard;

